import { abilityDisplayName } from '../combat/fctLabel';

/**
 * The name float over a remote caster (lane AI, 2026-10-08).
 *
 * Two defects in one line of RemotePlayerVfxBridge: it printed the wire's
 * `ability_name`, which the server fills with the raw id when an ability has
 * no displayName (`basic_strike` in purple), and it spawned at the sprite's
 * y − 40, which is exactly the HP/MP bars. The name now comes from content by
 * id, and the float starts above the caster's overhead group — the group top
 * the overhead layout last drew (name, bars, cast row, after its nudge), the
 * same anchor chat bubbles use (plan 15).
 */

/** Gap between the float's bottom edge and the overhead group's top. */
export const REMOTE_CAST_NAME_GAP_PX = 4;
/** The 'ability' floating-text style draws at 18 px. */
export const REMOTE_CAST_NAME_FONT_PX = 18;
/** Where the float starts when the caster has no laid-out group. */
export const REMOTE_CAST_NAME_FALLBACK_LIFT_PX = 64;

export interface RemoteCastNameFloat {
  text: string;
  x: number;
  y: number;
}

/**
 * The server's `ability_name` is the TOML name on the cast-handler path, but
 * falls back to the raw id elsewhere (`ability.displayName ?? ability.id`,
 * CombatPipeline). A real server name wins (it can name a variant); a raw id
 * or a missing name resolves through content.
 */
export function remoteCastNameText(msg: { ability_id?: string; abilityId?: string; ability_name?: string } | null | undefined): string | null {
  const id = msg?.ability_id ?? msg?.abilityId;
  const wire = msg?.ability_name;
  if (wire && wire !== id && !/^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(wire)) return wire;
  if (id) return abilityDisplayName(id);
  return wire ? abilityDisplayName(wire) : null;
}

/** Spawn point: centred above the group top (or the fallback lift without one). */
export function remoteCastNameFloat(
  text: string,
  sprite: { x: number; y: number },
  groupTop: number | null,
): RemoteCastNameFloat {
  const y = groupTop === null
    ? sprite.y - REMOTE_CAST_NAME_FALLBACK_LIFT_PX
    : groupTop - REMOTE_CAST_NAME_GAP_PX - REMOTE_CAST_NAME_FONT_PX / 2;
  return { text, x: sprite.x, y };
}
